import { IConfigIssue, IEditorPolicy } from '../model/types';

export const GENERIC_SAVE_ERROR = 'Saving the draft failed. Please try again.';
export const GENERIC_PUBLISH_ERROR = 'Publishing failed. Please try again.';
export const GENERIC_RESTORE_ERROR = 'Restoring the version failed. Please try again.';
export const GENERIC_METADATA_ERROR = 'Saving the name and comment failed. Please try again.';

interface IApiErrorPayload {
  message?: string;
  errors?: Record<string, string | string[]>;
}

const getApiPayload = (error: unknown): IApiErrorPayload | null => {
  if (typeof error !== 'object' || error === null || !('data' in error)) return null;
  const data = (error as { data: unknown }).data;

  return typeof data === 'object' && data !== null ? (data as IApiErrorPayload) : null;
};

const getErrorStatus = (error: unknown): number | undefined =>
  typeof error === 'object' && error !== null && 'status' in error
    ? ((error as { status?: unknown }).status as number)
    : undefined;

// A missing resource: "no active config yet" for the active endpoint, an unknown
// id for the by-id one — distinct from "unavailable" (any other failure).
export const isNotFoundError = (error: unknown): boolean => getErrorStatus(error) === 404;

// RTK Query error → issues: field errors are read only from a 422 (the validation
// status), so a 500 that happens to carry an `errors` object still falls back to
// the server message. Anything else becomes one path-less issue carrying the
// caller's fallback (save vs publish wording).
export const parseConfigPathErrors = (
  error: unknown,
  fallback: string = GENERIC_SAVE_ERROR,
): IConfigIssue[] => {
  const payload = getApiPayload(error);
  const entries = getErrorStatus(error) === 422 ? Object.entries(payload?.errors ?? {}) : [];

  if (entries.length) {
    return entries.map(([path, messages]) => ({
      path,
      message: (Array.isArray(messages) ? messages[0] : messages) || fallback,
    }));
  }

  return [{ path: '', message: payload?.message || fallback }];
};

const CONFIG_PATH = /^policies\.(\d+)(?:\.rules\.(\d+))?(?:\.(.+))?$/;

// "policies.1.rules.0.max" → 'Policy “X” → rule “Y”: <message>'; non-config paths
// render the message alone.
export const describeIssue = (issue: IConfigIssue, policies: IEditorPolicy[]): string => {
  const match = CONFIG_PATH.exec(issue.path);
  if (!match) return issue.message;

  const policy = policies[Number(match[1])];
  if (!policy) return `${issue.path}: ${issue.message}`;

  const rule = match[2] !== undefined ? policy.rules[Number(match[2])] : undefined;
  const policyLabel = `Policy “${policy.name || `#${Number(match[1]) + 1}`}”`;
  const ruleLabel = rule ? ` → rule “${rule.name || `#${Number(match[2]) + 1}`}”` : '';

  return `${policyLabel}${ruleLabel}: ${issue.message}`;
};
